/**
 * Hook to sync cart state with the database.
 *
 * The database stores asset IDs with item_source ('ie' or 'additional').
 * The API returns full asset entities with item_source for filtering.
 */

import { useCallback, useMemo } from 'react';
import { useWorkflowCart } from '@/hooks/queries/cart/useCartQueries';
import {
  useAddToCart,
  useRemoveFromCart,
  useBulkAllocateToCart,
} from '@/hooks/mutations/cart/useCartMutations';
import type { Entity } from '@/types/Entity';
import type {
  ReservedAsset,
  BulkAllocateItem,
  BulkAllocateResult,
  SizeAllocationCart,
} from '@/services/cart';
import type { IssuePlannerCartItem, IssueAdditionalCartItem } from './types';

/**
 * Group assets by catalog_id into IssuePlannerCartItem format for IE Kit tab.
 */
const groupAssetsForIe = (assets: ReservedAsset[]): IssuePlannerCartItem[] => {
  const byGroup = new Map<string, ReservedAsset[]>();

  for (const asset of assets) {
    const catalogId = (asset.entity.entity_data?.catalog_id as string) ?? asset.entity.id;
    const ieKitId = asset.ie_kit_id || 'none';
    const key = `${catalogId}::${ieKitId}`;

    const existing = byGroup.get(key) ?? [];
    existing.push(asset);
    byGroup.set(key, existing);
  }

  const items: IssuePlannerCartItem[] = [];
  for (const [key, groupAssets] of byGroup) {
    const first = groupAssets[0];
    if (!first) continue;

    const catalogId = (first.entity.entity_data?.catalog_id as string) ?? first.entity.id;
    const ieKitId = first.ie_kit_id;
    const sourceLocationIds = Array.from(
      new Set(
        groupAssets
          .map((asset) =>
            typeof asset.entity.entity_data?.location_id === 'string'
              ? asset.entity.entity_data.location_id.trim()
              : '',
          )
          .filter((id) => id.length > 0),
      ),
    );

    items.push({
      id: key, // Unique ID for the cart row
      plannerItemId: ieKitId ? `${catalogId}-${ieKitId}` : catalogId, // Matches buildPlannerItemId logic
      name: first.entity.display_name ?? (first.entity.entity_data?.item as string) ?? '',
      lin: (first.entity.entity_data?.lin as string) ?? '',
      nsn: (first.entity.entity_data?.nsn as string) ?? null,
      assetIds: groupAssets.map((a) => a.entity.id),
      sourceLocationIds,
      selectedCatalogId: catalogId,
      sizeLabel: first.size ?? (first.entity.entity_data?.size as string) ?? null,
      ieKitId: ieKitId,
    });
  }

  return items;
};

/**
 * Group assets by catalog_id into IssueAdditionalCartItem format for Additional tab.
 */
const groupAssetsForAdditional = (assets: ReservedAsset[]): IssueAdditionalCartItem[] => {
  const byCatalog = new Map<string, ReservedAsset[]>();

  for (const asset of assets) {
    const catalogId = (asset.entity.entity_data?.catalog_id as string) ?? asset.entity.id;
    const existing = byCatalog.get(catalogId) ?? [];
    existing.push(asset);
    byCatalog.set(catalogId, existing);
  }

  const items: IssueAdditionalCartItem[] = [];
  for (const [catalogId, groupAssets] of byCatalog) {
    const first = groupAssets[0];
    if (!first) continue;

    const sourceLocationIds = Array.from(
      new Set(
        groupAssets
          .map((asset) =>
            typeof asset.entity.entity_data?.location_id === 'string'
              ? asset.entity.entity_data.location_id.trim()
              : '',
          )
          .filter((id) => id.length > 0),
      ),
    );

    items.push({
      groupId: catalogId,
      name: first.entity.display_name ?? (first.entity.entity_data?.item as string) ?? '',
      source: 'Additional inventory',
      assetIds: groupAssets.map((a) => a.entity.id),
      sourceLocationIds,
      lin: (first.entity.entity_data?.lin as string) ?? null,
      nsn: (first.entity.entity_data?.nsn as string) ?? null,
      sizeLabel: first.size ?? (first.entity.entity_data?.size as string) ?? null,
    });
  }

  return items;
};

export interface UseDbCartSyncResult {
  /** All reserved asset IDs from the database cart (for this tab) */
  reservedAssetIds: string[];
  /** Full asset entities from the cart (for this tab) */
  assets: Entity[];
  /** Placeholders for unsized items awaiting size selection */
  placeholders: SizeAllocationCart[];
  /** All reserved asset IDs across all tabs */
  allReservedAssetIds: string[];
  /** Whether the cart is loading */
  isLoading: boolean;
  /** Add assets to cart */
  addToCart: (assetIds: string[], ieKitId?: string) => Promise<boolean>;
  /** Remove specific assets from cart */
  removeFromCart: (assetIds: string[]) => Promise<boolean>;
  /** Clear items from this tab's cart */
  clearCart: () => Promise<boolean>;
  /** Bulk allocate assets by LINs (single RPC call) */
  bulkAllocateToCart: (items: BulkAllocateItem[], options?: { silent?: boolean }) => Promise<BulkAllocateResult | null>;
  /** Refresh cart data */
  refetch: () => Promise<void>;
}

// Specialized hooks for IE Kit and Additional tabs with grouped items
export interface UseIeDbCartSyncResult extends UseDbCartSyncResult {
  cartItems: IssuePlannerCartItem[];
}

export interface UseAdditionalDbCartSyncResult extends UseDbCartSyncResult {
  cartItems: IssueAdditionalCartItem[];
}

/**
 * Hook for IE Kit tab - filters to assets with item_source='ie'.
 */
export const useIeDbCartSync = (
  customerId: string | undefined,
  enabled: boolean,
): UseIeDbCartSyncResult => {
  const { data: cartData, isLoading, refetch } = useWorkflowCart(enabled ? customerId : undefined);
  const addToCartMutation = useAddToCart();
  const removeFromCartMutation = useRemoveFromCart();
  const bulkAllocateMutation = useBulkAllocateToCart();

  // Get all reserved assets
  const allReservedAssets = useMemo(
    () => (cartData?.assets ?? []) as ReservedAsset[],
    [cartData?.assets],
  );

  const placeholders = useMemo(
    () => (cartData?.placeholders ?? []) as SizeAllocationCart[],
    [cartData?.placeholders],
  );

  // Get all reserved asset IDs (across all tabs)
  const allReservedAssetIds = useMemo(
    () => allReservedAssets.map((a) => a.entity.id),
    [allReservedAssets],
  );

  // Filter to IE Kit assets only
  const ieReservedAssets = useMemo(
    () => allReservedAssets.filter((a) => a.item_source === 'ie'),
    [allReservedAssets],
  );

  const assets = useMemo(() => ieReservedAssets.map((a) => a.entity), [ieReservedAssets]);

  const reservedAssetIds = useMemo(() => assets.map((a) => a.id), [assets]);

  const cartItems = useMemo(() => groupAssetsForIe(ieReservedAssets), [ieReservedAssets]);

  // Add to cart with item_source='ie'
  const addToCart = useCallback(
    async (assetIds: string[], ieKitId?: string): Promise<boolean> => {
      if (!customerId || assetIds.length === 0) return false;
      try {
        const result = await addToCartMutation.mutateAsync({
          customerId,
          assetIds,
          itemSource: 'ie',
          ieKitId,
        });
        return result.success;
      } catch {
        return false;
      }
    },
    [addToCartMutation, customerId],
  );

  // Remove from cart
  const removeFromCart = useCallback(
    async (assetIds: string[]): Promise<boolean> => {
      if (!customerId || assetIds.length === 0) return false;
      try {
        const result = await removeFromCartMutation.mutateAsync({
          customerId,
          assetIds,
        });
        return result.success;
      } catch {
        return false;
      }
    },
    [removeFromCartMutation, customerId],
  );

  // Clear IE Kit cart items only
  const clearCart = useCallback(async (): Promise<boolean> => {
    if (!customerId || reservedAssetIds.length === 0) return true;
    return removeFromCart(reservedAssetIds);
  }, [customerId, reservedAssetIds, removeFromCart]);

  // Bulk allocate assets by LINs in a single RPC call
  const bulkAllocateToCart = useCallback(
    async (items: BulkAllocateItem[], options?: { silent?: boolean }): Promise<BulkAllocateResult | null> => {
      if (!customerId || items.length === 0) return null;
      try {
        const result = await bulkAllocateMutation.mutateAsync({
          customerId,
          items,
          excludeAssetIds: allReservedAssetIds,
          silent: options?.silent,
        });
        return result;
      } catch {
        return null;
      }
    },
    [bulkAllocateMutation, customerId, allReservedAssetIds],
  );

  return {
    reservedAssetIds,
    assets,
    placeholders,
    allReservedAssetIds,
    cartItems,
    isLoading,
    addToCart,
    removeFromCart,
    clearCart,
    bulkAllocateToCart,
    refetch: async () => { await refetch(); },
  };
};

/**
 * Hook for Additional Items tab - filters to assets with item_source='additional'.
 */
export const useAdditionalDbCartSync = (
  customerId: string | undefined,
  enabled: boolean,
): UseAdditionalDbCartSyncResult => {
  const { data: cartData, isLoading, refetch } = useWorkflowCart(enabled ? customerId : undefined);
  const addToCartMutation = useAddToCart();
  const removeFromCartMutation = useRemoveFromCart();

  // Get all reserved assets
  const allReservedAssets = useMemo(
    () => (cartData?.assets ?? []) as ReservedAsset[],
    [cartData?.assets],
  );

  const placeholders = useMemo(
    () => (cartData?.placeholders ?? []) as SizeAllocationCart[],
    [cartData?.placeholders],
  );

  // Get all reserved asset IDs (across all tabs)
  const allReservedAssetIds = useMemo(
    () => allReservedAssets.map((a) => a.entity.id),
    [allReservedAssets],
  );

  // Filter to additional assets only
  const additionalReservedAssets = useMemo(
    () => allReservedAssets.filter((a) => a.item_source === 'additional'),
    [allReservedAssets],
  );

  const assets = useMemo(
    () => additionalReservedAssets.map((a) => a.entity),
    [additionalReservedAssets],
  );

  const reservedAssetIds = useMemo(() => assets.map((a) => a.id), [assets]);

  const cartItems = useMemo(
    () => groupAssetsForAdditional(additionalReservedAssets),
    [additionalReservedAssets],
  );

  // Add to cart with item_source='additional'
  const addToCart = useCallback(
    async (assetIds: string[]): Promise<boolean> => {
      if (!customerId || assetIds.length === 0) return false;
      try {
        const result = await addToCartMutation.mutateAsync({
          customerId,
          assetIds,
          itemSource: 'additional',
        });
        return result.success;
      } catch {
        return false;
      }
    },
    [addToCartMutation, customerId],
  );

  // Remove from cart
  const removeFromCart = useCallback(
    async (assetIds: string[]): Promise<boolean> => {
      if (!customerId || assetIds.length === 0) return false;
      try {
        const result = await removeFromCartMutation.mutateAsync({
          customerId,
          assetIds,
        });
        return result.success;
      } catch {
        return false;
      }
    },
    [removeFromCartMutation, customerId],
  );

  // Clear additional cart items only
  const clearCart = useCallback(async (): Promise<boolean> => {
    if (!customerId || reservedAssetIds.length === 0) return true;
    return removeFromCart(reservedAssetIds);
  }, [customerId, reservedAssetIds, removeFromCart]);

  // Bulk allocate is not used for additional items tab
  const bulkAllocateToCart = useCallback(async (): Promise<BulkAllocateResult | null> => null, []);

  return {
    reservedAssetIds,
    assets,
    placeholders,
    allReservedAssetIds,
    cartItems,
    isLoading,
    addToCart,
    removeFromCart,
    clearCart,
    bulkAllocateToCart,
    refetch: async () => { await refetch(); },
  };
};
